import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Film, Loader2, Trash2, AlertTriangle, Clock } from 'lucide-react';
import type { MonitorAlert } from '../../shared/protocol';
import { useSubPath } from '../lib/useSubPath';
import { api } from '../lib/api';
import { AlertChips, alertTotal } from './AlertChips';

interface Summary {
  id: string;
  pin: string;
  quizTitle: string;
  playerId: string;
  playerName: string;
  isGuest: boolean;
  score: number;
  durationMs: number;
  eventCount: number;
  truncated: boolean;
  alerts: MonitorAlert;
  createdAt: string;
}

const formatDuration = (ms: number) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Grabaciones guardadas de las salas supervisadas. Solo las ve quien abrió la
 * sala; se borran solas a los 30 días.
 */
export const RecordingsView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [list, setList] = useState<Summary[] | null>(null);
  const [retention, setRetention] = useState(30);
  const [error, setError] = useState('');
  // La grabación abierta va en la dirección (/grabaciones/<id>).
  const [[openId = null], setSub] = useSubPath('/grabaciones');
  const setOpenId = (id: string | null, opts?: { replace?: boolean }) => setSub(id ? [id] : [], opts);

  const load = () =>
    api
      .get<{ recordings: Summary[]; retentionDays: number }>('/recordings')
      .then((r) => {
        setList(r.recordings);
        setRetention(r.retentionDays);
      })
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void load();
  }, []);

  // Una partida = mismo PIN y mismo momento de guardado.
  const games = useMemo(() => {
    const map = new Map<string, Summary[]>();
    for (const r of list ?? []) {
      const key = `${r.pin}|${r.createdAt}`;
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return [...map.entries()].map(([key, rows]) => ({
      key,
      rows: rows.sort((a, b) => alertTotal(b.alerts) - alertTotal(a.alerts) || a.playerName.localeCompare(b.playerName)),
    }));
  }, [list]);

  const removeOne = async (id: string) => {
    if (!window.confirm('¿Borrar esta grabación? No se puede deshacer.')) return;
    await api.delete(`/recordings/${id}`).catch(() => undefined);
    if (openId === id) setOpenId(null, { replace: true });
    void load();
  };

  const removeGame = async (rows: Summary[]) => {
    if (!window.confirm(`¿Borrar las ${rows.length} grabaciones de esta partida?`)) return;
    await api
      .post('/recordings/delete-game', { pin: rows[0].pin, createdAt: rows[0].createdAt })
      .catch(() => undefined);
    setOpenId(null, { replace: true });
    void load();
  };

  const opened = list?.find((r) => r.id === openId);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={opened ? () => setOpenId(null) : onBack}
          className="p-2 rounded-lg text-ink-400 hover:text-ink-100 hover:bg-ink-800 transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Film className="w-6 h-6 text-sky-400" /> Grabaciones
          </h1>
          <p className="text-ink-500 text-sm">
            Pantallas de tus partidas supervisadas. Se borran solas a los {retention} días.
          </p>
        </div>
      </div>

      {error && <p className="text-danger-400">{error}</p>}
      {!list && !error && <Loader2 className="w-6 h-6 animate-spin text-ink-400" />}

      {opened ? (
        <Replay summary={opened} onDelete={() => void removeOne(opened.id)} />
      ) : (
        list &&
        (games.length === 0 ? (
          <div className="surface p-10 text-center text-ink-400">
            Aún no hay grabaciones. Actívalas con "Supervisar y grabar pantallas" al abrir una sala.
          </div>
        ) : (
          games.map(({ key, rows }) => (
            <section key={key} className="surface p-4 space-y-3">
              <header className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold truncate">{rows[0].quizTitle}</h2>
                  <p className="text-ink-500 text-xs flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {formatDate(rows[0].createdAt)} · PIN {rows[0].pin} ·{' '}
                    {rows.length} {rows.length === 1 ? 'jugador' : 'jugadores'}
                  </p>
                </div>
                <button
                  onClick={() => void removeGame(rows)}
                  className="p-2 rounded-lg text-ink-500 hover:text-danger-400 hover:bg-danger-500/10"
                  aria-label="Borrar todas las grabaciones de esta partida"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </header>

              <ul className="space-y-1.5">
                {rows.map((r) => (
                  <li key={r.id}>
                    <button
                      onClick={() => setOpenId(r.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left border transition-colors hover:border-sky-600 ${
                        alertTotal(r.alerts) > 0 ? 'border-ember-600/60 bg-ember-500/5' : 'border-ink-700 bg-ink-850'
                      }`}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-ink-100 truncate">
                          {r.playerName}
                          {r.isGuest && <span className="text-ink-500 text-xs"> (invitado)</span>}
                        </span>
                        <AlertChips alert={r.alerts} />
                      </span>
                      {alertTotal(r.alerts) > 0 && <AlertTriangle className="w-4 h-4 text-ember-400 shrink-0" />}
                      <span className="text-ink-400 text-xs tabular-nums shrink-0">
                        {r.score.toLocaleString('es')} pts · {formatDuration(r.durationMs)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        ))
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Reproductor
 * ------------------------------------------------------------------ */

const FLAG_LABEL: Record<string, string> = {
  hidden: 'Salió de la pestaña',
  blur: 'Perdió el foco',
  paste: 'Pegó texto',
  copy: 'Copió texto',
};
const FLAG_COLOR: Record<string, string> = {
  hidden: '#F97316',
  blur: '#F59E0B',
  paste: '#E1467C',
  copy: '#8B5CF6',
};

interface PlayerHandle {
  goto: (offsetMs: number, play?: boolean) => void;
  $destroy?: () => void;
}

const Replay: React.FC<{ summary: Summary; onDelete: () => void }> = ({ summary, onDelete }) => {
  const host = useRef<HTMLDivElement>(null);
  const player = useRef<PlayerHandle | null>(null);
  const [marks, setMarks] = useState<{ kind: string; offset: number }[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const [{ events }, { default: RRWebPlayer }] = await Promise.all([
          api.get<{ events: { type: number; timestamp: number; data?: Record<string, unknown> }[] }>(
            `/recordings/${summary.id}`,
          ),
          import('rrweb-player'),
        ]);
        await import('rrweb-player/dist/style.css');
        if (cancelled || !host.current) return;

        const t0 = events[0]?.timestamp ?? 0;
        setMarks(
          events
            .filter((e) => e.type === 5 && typeof e.data?.tag === 'string' && e.data.tag in FLAG_LABEL)
            .map((e) => ({ kind: e.data!.tag as string, offset: e.timestamp - t0 })),
        );

        const meta = events.find((e) => e.type === 4)?.data as { width?: number; height?: number } | undefined;
        const width = Math.min(host.current.clientWidth || 800, 900);
        const ratio = meta?.width && meta?.height ? meta.height / meta.width : 0.6;

        host.current.innerHTML = '';
        player.current = new RRWebPlayer({
          target: host.current,
          props: {
            events: events as never,
            width,
            height: Math.round(width * ratio),
            autoPlay: false,
            showController: true,
            speedOption: [1, 2, 4, 8],
            tags: FLAG_COLOR,
          },
        }) as unknown as PlayerHandle;
        setLoading(false);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar la grabación.');
      }
    })();

    return () => {
      cancelled = true;
      player.current?.$destroy?.();
      player.current = null;
    };
  }, [summary.id]);

  return (
    <div className="space-y-4">
      <div className="surface p-4 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-lg truncate">{summary.playerName}</h2>
          <p className="text-ink-500 text-xs">
            {summary.quizTitle} · {formatDate(summary.createdAt)} · {summary.score.toLocaleString('es')} pts ·{' '}
            {formatDuration(summary.durationMs)}
          </p>
          <AlertChips alert={summary.alerts} showZero />
          {summary.truncated && (
            <p className="text-ember-300 text-xs mt-2">
              La grabación se cortó por tamaño: no incluye el final de la partida.
            </p>
          )}
        </div>
        <button
          onClick={onDelete}
          className="p-2 rounded-lg text-ink-500 hover:text-danger-400 hover:bg-danger-500/10"
          aria-label="Borrar esta grabación"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {error && <p className="text-danger-400">{error}</p>}
      {loading && !error && (
        <p className="text-ink-400 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando grabación…
        </p>
      )}

      <div ref={host} className="flex justify-center overflow-x-auto" />

      {marks.length > 0 && (
        <div className="surface p-4 space-y-2">
          <h3 className="text-sm uppercase tracking-wider text-ink-400">Avisos en la línea de tiempo</h3>
          <ul className="flex flex-wrap gap-2">
            {marks.map((m, i) => (
              <li key={i}>
                <button
                  onClick={() => player.current?.goto(Math.max(0, m.offset - 2000), true)}
                  className="chip border-ember-500 bg-ember-500/15 text-ember-300 hover:bg-ember-500/25"
                >
                  {formatDuration(m.offset)} · {FLAG_LABEL[m.kind]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
